import { Message, MessageAttachment, MessageType } from "@common/types/chat";
import { pluralizeWithCount } from "@common/helpers/string";
import React, { useState, useCallback, useEffect, useEffectEvent } from "react";
import { useUser } from "@hooks/useUser";
import classNames from "classnames";
import { TaskType } from "@common/types/task";
import { useOverlays } from "contexts/OverlayContext";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { KickAvatar } from "@components/avatar/KickAvatar";
import {
    KICK_ACTOR_LABEL,
    KICK_AGENT_ACTOR_LABEL,
    PLATFORM_API_ACTOR_LABEL,
} from "@common/constants/actor";
import actorIconStyles from "@components/history/components/ActorIcon.module.scss";
import { useIsFirmMemberOrStandaloneUser } from "@hooks/useIsFirmMemberOrStandaloneUser";
import { MessageAuthor, resolveMessageAuthor } from "./messageAuthor";
import {
    useUpdateMessage,
    useDeleteMessage,
    useResolveMessage,
    useUnresolveMessage,
} from "@api/chat.api";
import { Menu } from "@design-system/Menu/Menu.lib";
import {
    CheckIcon,
    CliIcon,
    CollapseIcon,
    DocumentIcon,
    ExpandIcon,
    HideIcon,
    ReplyIcon,
    ResolvedCommentIcon,
} from "@icons";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { SpreadsheetReference } from "@common/schemas/spreadsheetReference.schema";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { HStack } from "@design-system/HStack/HStack";
import { RelativeTime } from "@design-system/RelativeTime/RelativeTime";
import { Tag } from "@design-system/Tag/Tag";
import {
    deserializeSlateValue,
    serializeSlateValue,
    slateToPlainText,
    toggleChecklistItemAtPath,
} from "./slate/slateHelpers";
import { SlateValue } from "./slate/slateTypes";
import styles from "./MessageBubble.module.scss";
import { CustomMessageBubbleContent } from "./customMessages/CustomMessageBubbleContent";
import { AuditLogsMessage } from "./customMessages/AuditLogsMessage";
import { buildRefKey } from "./spreadsheetRefUtils";
import { SpreadsheetRefChip } from "./SpreadsheetRefChip";
import { MessageTransactionRefChip } from "./TransactionRefChip";
import { MessageCounterpartyRefChip } from "./CounterpartyRefChip";
import { MessageInvoiceRefChip } from "./InvoiceRefChip";
import { MessageDocumentRefChip } from "./DocumentRefChip";
import { useSpreadsheetRefFocus } from "./useSpreadsheetRefFocus";

/**
 * Chip used inside message bubbles.
 *
 * Highlight rules:
 * - Legacy chip (no viewConfig) → always highlights on hover.
 * - viewConfig set + matches live task body config → highlights on hover.
 * - viewConfig set + doesn’t match (or task body not mounted) → no highlight;
 *   chip shows a tooltip and becomes clickable to restore the saved view.
 *
 * Tooltip fix: the tooltip trigger cannot attach a ref to SpreadsheetRefChip
 * (it does not accept a ref prop). We wrap the chip in a <span> so that Base UI
 * can anchor the tooltip to a real DOM node, and place the click on the
 * wrapper so the chip’s own event handling is untouched.
 */
const MessageSpreadsheetRefChip: React.FC<{
    reference: SpreadsheetReference;
}> = React.memo(function MessageSpreadsheetRefChip({ reference }) {
    const {
        isViewMismatch,
        canHighlight,
        highlight,
        clearHighlight,
        applyView,
    } = useSpreadsheetRefFocus(reference);

    // Clear highlights when the chip unmounts while the cursor is over it.
    const createUnmountCleanup = useEffectEvent(() => () => {
        if (canHighlight) {
            clearHighlight();
        }
    });
    useEffect(() => createUnmountCleanup(), []);

    if (!isViewMismatch) {
        // No mismatch: plain chip with highlight handlers.
        return (
            <SpreadsheetRefChip
                reference={reference}
                onMouseEnter={canHighlight ? highlight : undefined}
                onMouseLeave={canHighlight ? clearHighlight : undefined}
            />
        );
    }

    // Mismatch: wrap in a <span> so the tooltip trigger can attach its ref to a
    // real DOM node, and put the click on the wrapper.
    return (
        <Tooltip content="View has changed — click to restore">
            <span onClick={applyView} style={{ cursor: "pointer" }}>
                <SpreadsheetRefChip reference={reference} />
            </span>
        </Tooltip>
    );
});

/**
 * Attachment pill inside a message bubble. Live documents open the document
 * overlay on click; deleted documents render as non-clickable tombstones
 * with the original filename.
 */
const MessageAttachmentPill: React.FC<{
    attachment: MessageAttachment;
}> = React.memo(function MessageAttachmentPill({ attachment }) {
    const { openOverlay } = useOverlays();
    const { financialDocumentId } = attachment;
    const isClickable = financialDocumentId != null && !attachment.isDeleted;

    const handleClick = useCallback(() => {
        if (financialDocumentId != null) {
            openOverlay("financialDocumentId", financialDocumentId);
        }
    }, [openOverlay, financialDocumentId]);

    return (
        <Tag
            size="md"
            color="primary"
            outline
            leftIcon={<DocumentIcon />}
            onClick={isClickable ? handleClick : undefined}
            className={
                attachment.isDeleted ? styles.mutedAttachment : undefined
            }
        >
            {attachment.isDeleted ? (
                <span className={styles.deletedAttachmentContent}>
                    <span className={styles.mutedAttachmentFilename}>
                        {attachment.fileName}
                    </span>
                    <span className={styles.deletedAttachmentNote}>
                        Deleted
                    </span>
                </span>
            ) : (
                attachment.fileName
            )}
        </Tag>
    );
});

const NON_USER_AUTHOR_LABELS: Record<
    Exclude<MessageAuthor["kind"], "user">,
    string
> = {
    agent: KICK_AGENT_ACTOR_LABEL,
    kick: KICK_ACTOR_LABEL,
    api: PLATFORM_API_ACTOR_LABEL,
};

const MessageAuthorAvatar = ({ author }: { author: MessageAuthor }) => {
    switch (author.kind) {
        case "user":
            return <UserAvatar user={author.user} size="sm" aria-hidden />;
        case "api":
            return (
                <CliIcon
                    className={actorIconStyles.glyphIcon}
                    data-size="sm"
                    aria-hidden
                />
            );
        case "agent":
        case "kick":
            return <KickAvatar size="sm" aria-hidden />;
    }
};

export const MessageBubble: React.FC<{
    message: Message;
    onReply?: (messageId: string) => void;
    isThreadContext?: boolean;
    showReplyFooter?: boolean;
    taskId?: string;
}> = ({
    message,
    onReply,
    isThreadContext = false,
    showReplyFooter = true,
    taskId,
}) => {
    const user = useUser();
    const { openOverlay } = useOverlays();
    const { isViewOnly, refuseIfViewOnly } = useViewOnlyRefusal();
    const canSeeAgentIdentity = useIsFirmMemberOrStandaloneUser();
    const author = resolveMessageAuthor({ message, canSeeAgentIdentity });
    const isOwnMessage = message.userId === user?.id;
    const isOwnStyled = isOwnMessage && author.kind !== "agent";
    const authorFirstName =
        author.kind === "user" ? author.user.firstName : null;
    const authorLabel =
        author.kind === "user"
            ? author.user.firstName
            : NON_USER_AUTHOR_LABELS[author.kind];
    const [isEditing, setIsEditing] = useState(false);
    const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
    const [isResolvedExpanded, setIsResolvedExpanded] = useState(false);
    const updateMessage = useUpdateMessage();
    const deleteMessage = useDeleteMessage();
    const resolveMessage = useResolveMessage();
    const unresolveMessage = useUnresolveMessage();
    const toaster = useToaster();
    const isDeleted = message.isDeleted === true;
    const isTopLevel = message.subthreadId == null;
    const isResolved = message.resolvedAt != null;
    const isAgentComment = author.kind === "agent";
    const canEdit =
        isOwnMessage &&
        !isAgentComment &&
        message.type === MessageType.SLATE &&
        !isDeleted;
    const canDelete = isOwnMessage && !isAgentComment && !isDeleted;
    const canResolve = isTopLevel && !isDeleted && !isResolved;
    const canUnresolve = isTopLevel && !isDeleted && isResolved;
    const canCreateTask = !isDeleted && message.type === MessageType.SLATE;
    const showResolvedCollapsed =
        isResolved && !isResolvedExpanded && !isThreadContext;
    const replyCount = message.replyCount ?? 0;
    const isEdited =
        !isDeleted &&
        message.updatedAt.getTime() !== message.createdAt.getTime();

    const handleSave = useCallback(
        (value: SlateValue) => {
            const content = serializeSlateValue(value);
            updateMessage.mutate({
                threadId: message.threadId,
                messageId: message.id,
                content,
            });
            setIsEditing(false);
        },
        [message.threadId, message.id, updateMessage],
    );

    const handleToggleCheck = useCallback(
        (path: number[]) => {
            if (isViewOnly) {
                refuseIfViewOnly();
                return;
            }
            if (!canEdit) {
                return;
            }
            const next = toggleChecklistItemAtPath(
                deserializeSlateValue(message.content),
                path,
            );
            updateMessage.mutate({
                threadId: message.threadId,
                messageId: message.id,
                content: serializeSlateValue(next),
            });
        },
        [
            canEdit,
            isViewOnly,
            message.content,
            message.id,
            message.threadId,
            refuseIfViewOnly,
            updateMessage,
        ],
    );

    const handleCancel = useCallback(() => {
        setIsEditing(false);
    }, []);

    const handleDeleteConfirm = useCallback(() => {
        deleteMessage.mutate(
            { threadId: message.threadId, messageId: message.id },
            { onSettled: () => setIsConfirmDeleteOpen(false) },
        );
    }, [deleteMessage, message.threadId, message.id]);

    const isAuditLogsMessage = message.type === MessageType.AUDIT_LOGS;
    if (isAuditLogsMessage) {
        return (
            <div className={styles.messageBubbleContainer}>
                <AuditLogsMessage
                    content={message.content}
                    actor={message.user ?? null}
                />
            </div>
        );
    }

    if (showResolvedCollapsed) {
        const totalCount = replyCount + 1;
        return (
            <div
                className={classNames(styles.messageBubbleContainer, {
                    [styles.isOwnMessage]: isOwnStyled,
                })}
            >
                <div
                    className={classNames(
                        styles.resolvedCollapsedContainer,
                        styles.messageBubble,
                        {
                            [styles.isOrganizationScopedMessage]:
                                message.organizationId,
                        },
                    )}
                    role="button"
                    tabIndex={0}
                    onClick={() => setIsResolvedExpanded(true)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setIsResolvedExpanded(true);
                        }
                    }}
                >
                    <div className={styles.resolvedCollapsedText}>
                        <ResolvedCommentIcon className="icon-color-gray-500" />
                        {pluralizeWithCount("resolved comment", totalCount)}
                        {authorFirstName ? ` from ${authorFirstName}` : ""}
                    </div>
                    <ExpandIcon className="icon-color-gray-500" />
                </div>
            </div>
        );
    }

    return (
        <>
            <div
                className={classNames(styles.messageBubbleContainer, {
                    [styles.isOwnMessage]: isOwnStyled,
                })}
            >
                <div
                    className={classNames(styles.messageBubble, {
                        [styles.isEditing]: isEditing,
                        [styles.isOrganizationScopedMessage]:
                            message.organizationId,
                    })}
                >
                    {isResolved && !isThreadContext && (
                        <div
                            className={styles.resolvedMessageSection}
                            role="button"
                            tabIndex={0}
                            onClick={() => setIsResolvedExpanded(false)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    setIsResolvedExpanded(false);
                                }
                            }}
                        >
                            <HStack gap={8} align="center">
                                <ResolvedCommentIcon className="icon-color-gray-500" />
                                Collapse
                            </HStack>
                            <div>
                                <CollapseIcon className="icon-color-gray-500" />
                            </div>
                        </div>
                    )}
                    <div className={styles.messageContent}>
                        <div className={styles.messageHeaderRow}>
                            <HStack gap={8} align="center">
                                <MessageAuthorAvatar author={author} />
                                <span className="text-gray-900 font-weight-400 line-height-1">
                                    {authorLabel}
                                </span>
                                <RelativeTime
                                    date={message.createdAt}
                                    className="text-xs text-gray-500 line-height-1"
                                />
                                {message.organizationId && (
                                    <Tooltip content="Only visible to your firm">
                                        <HideIcon className="icon-color-gray-500 line-height-1" />
                                    </Tooltip>
                                )}
                                {isEdited && (
                                    <span className="text-xs text-gray-400 line-height-1">
                                        (edited)
                                    </span>
                                )}
                            </HStack>
                            <HStack gap={4}>
                                {onReply && !isDeleted && (
                                    <Tooltip content="Reply">
                                        <Button
                                            iconOnly
                                            variant="secondary"
                                            onClick={() => onReply(message.id)}
                                            className={styles.showOnHover}
                                            size="sm"
                                            aria-label="Reply"
                                        >
                                            <ReplyIcon />
                                        </Button>
                                    </Tooltip>
                                )}

                                {canResolve && (
                                    <Tooltip content="Resolve">
                                        <Button
                                            iconOnly
                                            variant="secondary"
                                            onClick={() =>
                                                resolveMessage.mutate(
                                                    {
                                                        threadId:
                                                            message.threadId,
                                                        messageId: message.id,
                                                    },
                                                    {
                                                        onError: () => {
                                                            toaster.error(
                                                                "Failed to resolve message. Please try again.",
                                                            );
                                                        },
                                                    },
                                                )
                                            }
                                            disabled={resolveMessage.isPending}
                                            className={styles.showOnHover}
                                            size="sm"
                                            aria-label="Resolve"
                                        >
                                            <CheckIcon />
                                        </Button>
                                    </Tooltip>
                                )}
                                {!isEditing &&
                                    (canEdit ||
                                        canDelete ||
                                        canUnresolve ||
                                        canCreateTask) && (
                                        <Menu.More
                                            className={styles.showOnHover}
                                        >
                                            {canEdit && (
                                                <Menu.Item
                                                    onClick={() =>
                                                        setIsEditing(true)
                                                    }
                                                >
                                                    Edit
                                                </Menu.Item>
                                            )}
                                            {canDelete && (
                                                <Menu.Item
                                                    onClick={() =>
                                                        setIsConfirmDeleteOpen(
                                                            true,
                                                        )
                                                    }
                                                >
                                                    Delete
                                                </Menu.Item>
                                            )}
                                            {canUnresolve && (
                                                <Menu.Item
                                                    onClick={() =>
                                                        unresolveMessage.mutate(
                                                            {
                                                                threadId:
                                                                    message.threadId,
                                                                messageId:
                                                                    message.id,
                                                            },
                                                        )
                                                    }
                                                    disabled={
                                                        unresolveMessage.isPending
                                                    }
                                                >
                                                    Unresolve
                                                </Menu.Item>
                                            )}
                                            {canCreateTask && (
                                                <Menu.Item
                                                    onClick={() => {
                                                        // Opens a create form
                                                        // the server can only
                                                        // refuse on save
                                                        // (PRO-11345).
                                                        if (
                                                            refuseIfViewOnly()
                                                        ) {
                                                            return;
                                                        }
                                                        openOverlay(
                                                            "createTask",
                                                            {
                                                                type: TaskType.QUESTION,
                                                                initialValues: {
                                                                    title: slateToPlainText(
                                                                        message.content,
                                                                    ),
                                                                    messageId:
                                                                        message.id,
                                                                },
                                                                relatedTaskId:
                                                                    taskId,
                                                            },
                                                        );
                                                    }}
                                                >
                                                    Create task
                                                </Menu.Item>
                                            )}
                                        </Menu.More>
                                    )}
                            </HStack>
                        </div>
                        {isDeleted ? (
                            <span className={styles.deletedMessagePlaceholder}>
                                This message was deleted
                            </span>
                        ) : (
                            <CustomMessageBubbleContent
                                message={message}
                                author={author}
                                isEditing={isEditing}
                                onSave={handleSave}
                                onCancel={handleCancel}
                                onToggleCheck={
                                    canEdit ? handleToggleCheck : undefined
                                }
                            />
                        )}
                        {!isDeleted &&
                            message.spreadsheetReferences &&
                            message.spreadsheetReferences.length > 0 && (
                                <div className={styles.refsRow}>
                                    {message.spreadsheetReferences.map(
                                        (ref) => (
                                            <MessageSpreadsheetRefChip
                                                key={buildRefKey(ref)}
                                                reference={ref}
                                            />
                                        ),
                                    )}
                                </div>
                            )}
                        {!isDeleted &&
                            message.transactionReferences &&
                            message.transactionReferences.length > 0 && (
                                <div className={styles.refsRow}>
                                    {message.transactionReferences.map(
                                        (ref) => (
                                            <MessageTransactionRefChip
                                                key={`${ref.taskId}::${ref.transactionId}`}
                                                reference={ref}
                                            />
                                        ),
                                    )}
                                </div>
                            )}
                        {!isDeleted &&
                            message.counterpartyReferences &&
                            message.counterpartyReferences.length > 0 && (
                                <div className={styles.refsRow}>
                                    {message.counterpartyReferences.map(
                                        (ref) => (
                                            <MessageCounterpartyRefChip
                                                key={`${ref.taskId}::${ref.counterpartyId}`}
                                                reference={ref}
                                            />
                                        ),
                                    )}
                                </div>
                            )}
                        {!isDeleted &&
                            message.invoiceReferences &&
                            message.invoiceReferences.length > 0 && (
                                <div className={styles.refsRow}>
                                    {message.invoiceReferences.map((ref) => (
                                        <MessageInvoiceRefChip
                                            key={`${ref.taskId}::${ref.invoiceId}`}
                                            reference={ref}
                                        />
                                    ))}
                                </div>
                            )}
                        {!isDeleted &&
                            message.documentReferences &&
                            message.documentReferences.length > 0 && (
                                <div className={styles.refsRow}>
                                    {message.documentReferences.map((ref) => (
                                        <MessageDocumentRefChip
                                            key={`${ref.taskId}::${ref.documentId}`}
                                            reference={ref}
                                        />
                                    ))}
                                </div>
                            )}
                        {!isDeleted &&
                            message.attachments &&
                            message.attachments.length > 0 && (
                                <div className={styles.refsRow}>
                                    {message.attachments.map(
                                        (attachment, idx) => (
                                            <MessageAttachmentPill
                                                key={`${attachment.financialDocumentId ?? "deleted"}-${idx}`}
                                                attachment={attachment}
                                            />
                                        ),
                                    )}
                                </div>
                            )}
                    </div>
                    {showReplyFooter &&
                        isTopLevel &&
                        replyCount > 0 &&
                        onReply && (
                            <div
                                className={styles.replyFooter}
                                role="button"
                                tabIndex={0}
                                onClick={() => onReply?.(message.id)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        onReply?.(message.id);
                                    }
                                }}
                            >
                                <HStack gap={8} align="center">
                                    <div
                                        className={
                                            styles.repliesIndicatorContainer
                                        }
                                    >
                                        <div
                                            className={styles.repliesIndicator}
                                        />
                                    </div>
                                    <span className={styles.replyCountText}>
                                        {pluralizeWithCount(
                                            "reply",
                                            replyCount,
                                        )}
                                    </span>
                                </HStack>
                                {message.lastReplyAt && (
                                    <RelativeTime
                                        date={message.lastReplyAt}
                                        className={classNames(
                                            "text-xs text-gray-500 line-height-1",
                                            styles.lastReplyAtText,
                                        )}
                                    />
                                )}
                            </div>
                        )}
                </div>
            </div>
            {isConfirmDeleteOpen && (
                <ConfirmationModal
                    show={isConfirmDeleteOpen}
                    title="Delete this message?"
                    question="The message bubble will remain visible but without content."
                    yes="Delete message"
                    yesBtnVariant="destructive"
                    no="Cancel"
                    onConfirm={handleDeleteConfirm}
                    onReject={() => setIsConfirmDeleteOpen(false)}
                    onHide={() => setIsConfirmDeleteOpen(false)}
                />
            )}
        </>
    );
};
